import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowDown, ShieldCheck, Zap, Landmark } from 'lucide-react';

const HERO_IMG =
    'https://images.unsplash.com/photo-1528810289438-283f885c31ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBhcmNoaXRlY3R1cmUlMjBnbGFzcyUyMHNreXNjcmFwZXIlMjBiYW5rfGVufDB8fHxibHVlfDE3ODgzMzA0OTR8MA&ixlib=rb-4.1.0&q=85';

const Line = ({ children, delay }) => (
    <div className="overflow-hidden">
        <motion.span
            initial={{ y: '110%' }}
            animate={{ y: 0 }}
            transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
            className="block"
        >
            {children}
        </motion.span>
    </div>
);

const STATS = [
    { icon: Landmark, value: '9', label: 'Banking partners' },
    { icon: Zap, value: '48 hr', label: 'Avg. disbursal' },
    { icon: ShieldCheck, value: '100%', label: 'Transparent process' },
];

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ['start start', 'end start'],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
    const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

    return (
        <section
            ref={ref}
            id="top"
            data-testid="hero-section"
            className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-24 pb-16"
        >
            <div className="absolute inset-0 overflow-hidden">
                <motion.img
                    src={HERO_IMG}
                    alt="Corporate banking architecture"
                    style={{ y: imgY, scale: imgScale }}
                    className="w-full h-full object-cover opacity-25 grayscale"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#07090E]/70 via-[#07090E]/55 to-[#07090E]" />
                <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-emerald-500/10 blur-[140px]" />
            </div>

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    data-testid="hero-overline"
                    className="font-mono-x text-xs uppercase tracking-[0.3em] text-emerald-400 mb-6"
                >
                    Premier DSA Partner
                </motion.p>

                <h1
                    data-testid="hero-heading"
                    className="font-display font-extrabold tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-7xl"
                >
                    <Line delay={0.55}>Capital,</Line>
                    <Line delay={0.7}>
                        <span className="text-emerald-400 text-glow-emerald italic">engineered</span>
                    </Line>
                    <Line delay={0.85}>for your ambition.</Line>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1.15 }}
                    data-testid="hero-subtext"
                    className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-slate-300"
                >
                    Prem Associates connects you directly to nine of India's leading banks and
                    NBFCs — HDFC, ICICI, Axis, IndusInd, HDB Financial, Poonawalla Fincorp and
                    more. Faster approvals, sharper rates, zero running around.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1.3 }}
                    className="mt-9 flex flex-wrap items-center gap-4"
                >
                    <a
                        href="#apply"
                        data-testid="hero-apply-button"
                        className="rounded-full bg-emerald-500 px-7 py-3.5 text-sm font-semibold text-[#04110c] hover:bg-emerald-400 hover:shadow-[0_0_32px_rgba(16,185,129,0.45)] transition-all duration-300"
                    >
                        Check My Eligibility
                    </a>
                    <a
                        href="#loans"
                        data-testid="hero-explore-button"
                        className="rounded-full border border-white/15 px-7 py-3.5 text-sm font-semibold text-slate-200 hover:border-emerald-400/60 hover:text-emerald-300 transition-colors duration-300"
                    >
                        Explore Loans
                    </a>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 1.6 }}
                    className="mt-14 grid grid-cols-3 gap-6 max-w-lg border-t border-white/10 pt-7"
                    data-testid="hero-stats"
                >
                    {STATS.map(({ icon: Icon, value, label }) => (
                        <div key={label}>
                            <Icon className="w-4 h-4 text-emerald-400 mb-2" />
                            <div className="font-display text-xl sm:text-2xl font-bold">{value}</div>
                            <div className="text-[11px] sm:text-xs text-slate-500 mt-0.5">{label}</div>
                        </div>
                    ))}
                </motion.div>
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2, duration: 1 }}
                className="absolute bottom-6 left-1/2 -translate-x-1/2"
            >
                <ArrowDown className="w-4 h-4 text-slate-500 animate-bounce" />
            </motion.div>
        </section>
    );
};
